// AdvertQualityFacet — the body of the განცხადების ხარისხი / "Advert quality" section.
//
// It lives in its own file for ONE reason: the Desk is login-gated, so the only way to see
// a panel drawn is `harness.html`, which mounts components outside the gate. Keeping the
// JSX inline in IntakeTab would mean the harness rendered a COPY, and a copy drifts — the
// recurring disease in this codebase. The harness and the panel import this same function,
// so what the harness shows is what an agent sees.
//
// Props are the three things the facet list already has in scope: the committed filter
// object, its setter, and the translator. No fetching, no state — a facet body is a pure
// rendering of `filters`.
//
// ⚠️ Every string goes through `t()`. A key with no entry in a locale renders as the RAW
//    KEY on screen ("desk_photos_none") while every gate stays green, which is how "rows,
//    not constants" quietly became false on fifteen surfaces (MEETING_NUDGES_REVIEW).
//    Both locales are asserted by the i18n check; do not add a string here without both.

const PHOTO_BANDS = ['none', 'thin', 'ok']

export default function AdvertQualityFacet({ filters, set, t }) {
  return (
    <>
      {/* Photos — ONE band, never two checkboxes. "3 or fewer" strictly CONTAINS "none",
          so a pair of tickboxes could both be on and the combination would mean nothing.
          Measured on the live leads pool at ship: none 1,100 · ≤3 5,461 · 4+ 23,944, of
          29,993 clusters. (ok + thin < total by 588: those are clusters with no owner
          advert at all, which the roll-up deliberately omits rather than scoring zero.) */}
      <p className="text-xs text-muted-foreground mt-1 mb-1">{t('desk_photos_label')}</p>
      {PHOTO_BANDS.map(v => (
        <label key={v} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground cursor-pointer py-0.5">
          <input type="checkbox" checked={filters.photos === v}
            onChange={() => set('photos', filters.photos === v ? '' : v)} />
          <span className="flex-1 truncate">{t('desk_photos_' + v)}</span>
        </label>
      ))}

      {/* Description — empty across the five most recent scrapes, the same window the card
          itself reads. Reading only the LATEST scrape would claim 79% of the pool, because
          discovery re-scrapes without a description and becomes "latest". 3,767 clusters. */}
      <p className="text-xs text-muted-foreground mt-2 mb-1">{t('desk_quality_text_label')}</p>
      <label className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground cursor-pointer py-0.5">
        <input type="checkbox" checked={!!filters.noDescription}
          onChange={e => set('noDescription', e.target.checked)} />
        <span className="flex-1 truncate">{t('desk_no_description')}</span>
      </label>

      {/* Missing facts — measured per kind, never a bare null check. year_built is 0% on
          EVERY kind and deal, floors_total 54% on houses, bedrooms 12% on commercial;
          flagging those marks most of a category incomplete. 1,832 clusters. */}
      <label className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground cursor-pointer py-0.5">
        <input type="checkbox" checked={!!filters.missingFacts}
          onChange={e => set('missingFacts', e.target.checked)} />
        <span className="flex-1 truncate">{t('desk_missing_facts')}</span>
      </label>

      {/* Load-bearing, not a footnote. It is the one thing an agent cannot infer from the
          controls, and without it the counts read as wrong to anyone who has reposted a
          flat with better photos than the owner published. */}
      <p className="text-[11px] leading-snug text-muted-foreground/70 mt-2">{t('desk_quality_owner_note')}</p>
    </>
  )
}

// The badge the facet header shows. Exported so the header and the body cannot disagree
// about what "active" means — a facet showing no badge while silently filtering is exactly
// the lie the badge exists to stop.
export function advertQualityBadge(filters) {
  return (filters.photos ? 1 : 0)
    + (filters.noDescription ? 1 : 0)
    + (filters.missingFacts ? 1 : 0) || null
}
